import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'

/** A figure with the same figure from the period before it. On its own a number says nothing. */
function Stat({ label, value, previous, money = false }) {
  const shown = money ? `${Math.round(value).toLocaleString('mk-MK')} ден` : value.toLocaleString('mk-MK')

  // No percentage when there was nothing to compare against: "+∞%" is not a fact about anything.
  const delta = previous > 0 ? Math.round(((value - previous) / previous) * 100) : null

  return (
    <div className="stat">
      <div className="stat__value">{shown}</div>
      <div className="stat__label">{label}</div>
      {delta !== null && delta !== 0 && (
        <div className={`stat__delta ${delta > 0 ? 'up' : 'down'}`}>
          {delta > 0 ? '↑' : '↓'} {Math.abs(delta)}%
        </div>
      )}
    </div>
  )
}

export default function Dashboard({ onExpired }) {
  const { t } = useTranslation()
  const [days, setDays] = useState(7)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    api
      .dashboard(days)
      .then((result) => {
        setData(result)
        setError(null)
      })
      .catch((failure) => {
        onExpired(failure)
        setError(failure.message)
      })
  }, [days, onExpired])

  if (!data) return <p className="muted center">{error ?? t('common.loading')}</p>

  const { now, previous, totals, health } = data

  // What was earned against what the models cost, on the same rows. The margin is the answer to
  // the only question that matters at this size.
  const margin = now.revenueMkd > 0 ? Math.round(((now.revenueMkd - now.modelCostMkd) / now.revenueMkd) * 100) : null

  return (
    <>
      <div className="tabsrow">
        {[1, 7, 30, 90].map((option) => (
          <button
            key={option}
            type="button"
            className={`chip ${days === option ? 'on' : ''}`}
            onClick={() => setDays(option)}
          >
            {t('dashboard.days', { count: option })}
          </button>
        ))}
      </div>

      {error && <p className="status bad">{error}</p>}

      <section className="card">
        <h2>{t('dashboard.period')}</h2>
        <div className="stats">
          <Stat label={t('dashboard.newTenants')} value={now.tenants} previous={previous.tenants} />
          <Stat label={t('dashboard.active')} value={now.active} previous={previous.active} />
          <Stat label={t('dashboard.written')} value={now.written} previous={previous.written} />
          <Stat label={t('dashboard.published')} value={now.published} previous={previous.published} />
          <Stat label={t('dashboard.creditsSpent')} value={now.creditsSpent} previous={previous.creditsSpent} />
          <Stat label={t('dashboard.creditsSold')} value={now.creditsSold} previous={previous.creditsSold} />
          <Stat label={t('dashboard.revenue')} value={now.revenueMkd} previous={previous.revenueMkd} money />
          <Stat label={t('dashboard.modelCost')} value={now.modelCostMkd} previous={previous.modelCostMkd} money />
        </div>

        <p className="muted small">
          {margin === null ? t('dashboard.noRevenue') : t('dashboard.margin', { margin })}
        </p>
      </section>

      <section className="card">
        <h2>{t('dashboard.totals')}</h2>
        <div className="stats">
          <Stat label={t('dashboard.tenants')} value={totals.tenants} previous={0} />
          <Stat label={t('dashboard.suspended')} value={totals.suspended} previous={0} />
          <Stat label={t('dashboard.users')} value={totals.users} previous={0} />
          <Stat label={t('dashboard.postsAll')} value={totals.posts} previous={0} />
          <Stat label={t('dashboard.publishedAll')} value={totals.published} previous={0} />
          <Stat label={t('dashboard.creditsOut')} value={totals.creditsOutstanding} previous={0} />
        </div>
      </section>

      <section className="card">
        <h2>{t('dashboard.health')}</h2>

        {health.sitesFailed.length === 0 &&
          health.postsFailed.length === 0 &&
          health.tokensExpiring.length === 0 &&
          health.runsFailedToday === 0 && <p className="muted">{t('dashboard.allWell')}</p>}

        {health.runsFailedToday > 0 && (
          <p className="bad">{t('dashboard.runsFailed', { count: health.runsFailedToday })}</p>
        )}

        {health.sitesFailed.map((site) => (
          <p key={site.id} className="small">
            <a href={`#/tenant/${site.tenantId}`}>#{site.tenantId}</a> · {t('dashboard.siteFailed')} · {site.url}
            <span className="muted"> {site.lastError}</span>
          </p>
        ))}

        {health.postsFailed.map((post) => (
          <p key={post.id} className="small">
            <a href={`#/tenant/${post.tenantId}`}>#{post.tenantId}</a> · {t('dashboard.postFailed', { id: post.id })}
          </p>
        ))}

        {health.tokensExpiring.map((account) => (
          <p key={account.id} className="small">
            <a href={`#/tenant/${account.tenantId}`}>#{account.tenantId}</a> ·{' '}
            {t('dashboard.tokenExpiring', {
              name: account.name,
              network: account.network,
              when: new Date(account.tokenExpiresAt).toLocaleDateString('mk-MK'),
            })}
          </p>
        ))}

        {health.accountsNeedingReconnect > 0 && (
          <p className="bad small">{t('dashboard.reconnect', { count: health.accountsNeedingReconnect })}</p>
        )}
      </section>
    </>
  )
}
